// ============================================================
// CURRENT MISSION RENDERER
// Renders only from job.next_action.action
// Never render static buttons - everything is data-driven
// ============================================================

import React from 'react';
import { motion } from 'framer-motion';
import { 
  UserPlus, 
  CheckCircle, 
  Play, 
  FileCheck, 
  Receipt, 
  Lock,
  Clock,
  XCircle
} from 'lucide-react';
import { runJobAction, LHS_API } from '../utils/lhsCore';

const ACTION_CONFIG = {
  assign_contractors: {
    icon: UserPlus,
    color: 'bg-blue-600 hover:bg-blue-700',
    label: 'Assign Contractor',
  },
  approve_quote: {
    icon: CheckCircle,
    color: 'bg-purple-600 hover:bg-purple-700',
    label: 'Approve Quote',
  },
  start: {
    icon: Play,
    color: 'bg-indigo-600 hover:bg-indigo-700',
    label: 'Start Work',
  },
  complete: {
    icon: FileCheck,
    color: 'bg-green-600 hover:bg-green-700',
    label: 'Complete Job',
  },
  invoice: {
    icon: Receipt,
    color: 'bg-teal-600 hover:bg-teal-700',
    label: 'Submit Invoice',
  },
  close: {
    icon: Lock,
    color: 'bg-emerald-600 hover:bg-emerald-700',
    label: 'Close Job',
  },
  wait: {
    icon: Clock,
    color: 'bg-gray-400',
    label: 'Awaiting Response',
  },
  none: {
    icon: XCircle,
    color: 'bg-gray-300',
    label: 'No Action',
  },
  review: {
    icon: FileCheck,
    color: 'bg-yellow-600 hover:bg-yellow-700',
    label: 'Review Job',
  },
};

export default function CurrentMission({ 
  job, 
  onAssignContractors,
  onApproveQuote,
  onSubmitInvoice,
  onJobUpdated,
  onError,
  loading = false,
}) {
  const action = job?.next_action?.action;
  const label = job?.next_action?.label;

  if (!job || !job.next_action) return null;

  const config = ACTION_CONFIG[action] || ACTION_CONFIG.review;
  const Icon = config.icon;

  // Handle direct backend actions
  const handleDirectAction = async (actionType) => {
    try {
      let updatedJob;
      
      switch (actionType) {
        case 'start':
          updatedJob = await runJobAction(LHS_API.startJob(job.id));
          break;
        case 'complete':
          updatedJob = await runJobAction(LHS_API.completeJob(job.id));
          break;
        case 'close':
          updatedJob = await runJobAction(LHS_API.closeJob(job.id));
          break;
        default:
          return;
      }
      
      if (onJobUpdated) {
        onJobUpdated(updatedJob);
      }
    } catch (error) {
      if (onError) {
        onError(error.message);
      }
    }
  };

  // Terminal states - just show status
  if (action === 'none') {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-5"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${config.color} flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</div>
            <div className="font-semibold text-gray-800">{label}</div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Waiting state
  if (action === 'wait') {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-5"
      >
        <div className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-2">
          Current Mission
        </div>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <Clock className="w-5 h-5 text-amber-600 animate-pulse" />
          </div>
          <div>
            <div className="font-semibold text-gray-800">{label}</div>
            <div className="text-sm text-gray-500">Waiting for contractor response</div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Action buttons
  const renderActionButton = () => {
    const baseClasses = `w-full py-3.5 rounded-xl text-white font-semibold flex items-center justify-center gap-2 
                         transition-all duration-200 shadow-lg shadow-blue-500/20 
                         disabled:opacity-50 disabled:cursor-not-allowed`;

    switch (action) {
      case 'assign_contractors':
        return (
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => onAssignContractors?.(job)}
            disabled={loading}
            className={`${baseClasses} ${config.color}`}
            data-testid="mission-assign-btn"
          >
            <UserPlus className="w-5 h-5" />
            Assign Contractor
          </motion.button>
        );

      case 'approve_quote':
        return (
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => onApproveQuote?.(job)}
            disabled={loading}
            className={`${baseClasses} ${config.color}`}
            data-testid="mission-approve-btn"
          >
            <CheckCircle className="w-5 h-5" />
            Approve Quote
          </motion.button>
        );

      case 'start':
        return (
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => handleDirectAction('start')}
            disabled={loading}
            className={`${baseClasses} ${config.color}`}
            data-testid="mission-start-btn"
          >
            <Play className="w-5 h-5" />
            Start Work
          </motion.button>
        );

      case 'complete':
        return (
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => handleDirectAction('complete')}
            disabled={loading}
            className={`${baseClasses} ${config.color}`}
            data-testid="mission-complete-btn"
          >
            <FileCheck className="w-5 h-5" />
            Complete Job
          </motion.button>
        );

      case 'invoice':
        return (
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => onSubmitInvoice?.(job)}
            disabled={loading}
            className={`${baseClasses} ${config.color}`}
            data-testid="mission-invoice-btn"
          >
            <Receipt className="w-5 h-5" />
            Submit Invoice
          </motion.button>
        );

      case 'close':
        return (
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => handleDirectAction('close')}
            disabled={loading}
            className={`${baseClasses} ${config.color}`}
            data-testid="mission-close-btn"
          >
            <Lock className="w-5 h-5" />
            Close Job
          </motion.button>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl p-5 shadow-sm"
    >
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
        Current Mission
      </div>
      
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl ${config.color.split(' ')[0]} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="font-semibold text-gray-800">{label}</div>
      </div>

      {renderActionButton()}
    </motion.div>
  );
}
